import { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  useTheme, 
  Button, 
  TextField, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Fab,
  Fade,
  Slide,
  Backdrop
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LocalLibraryIcon from '@mui/icons-material/LocalLibrary';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { isAdminMode } from '../utils/auth';

interface Feature {
  id: number;
  icon: string;
  title: string;
  description: string;
}

interface FeatureFormData {
  icon: string;
  title: string;
  description: string;
}

const iconComponents: { [key: string]: React.ElementType } = {
  SchoolIcon: SchoolIcon,
  EmojiEventsIcon: EmojiEventsIcon,
  LocalLibraryIcon: LocalLibraryIcon
};

const iconOptions = [
  { value: 'SchoolIcon', label: 'School' },
  { value: 'EmojiEventsIcon', label: 'Trophy' },
  { value: 'LocalLibraryIcon', label: 'Library' }
];

const BASE_URL = import.meta.env.VITE_BASE_URL ?? '';

const WelcomeSection = () => {
  const [features, setFeatures] = useState<Feature[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [show, setShow] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFeature, setEditingFeature] = useState<Feature | null>(null);
  const [formData, setFormData] = useState<FeatureFormData>({
    icon: 'SchoolIcon',
    title: '',
    description: ''
  });

  useEffect(() => {
    fetchFeatures();
  }, []);

  const fetchFeatures = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BASE_URL}/api/Features`);
      if (!response.ok) throw new Error('Failed to fetch features');
      const data = await response.json();
      setFeatures(data);
    } catch (err: any) {
      setError(err.message);
      console.error('Failed to fetch features:', err);
    } finally {
      setLoading(false);
      setTimeout(() => setShow(true), 100);
    }
  };

  const openCreateDialog = () => {
    setEditingFeature(null);
    setFormData({
      icon: 'SchoolIcon',
      title: '',
      description: ''
    });
    setDialogOpen(true);
  };

  const openEditDialog = (feature: Feature) => {
    setEditingFeature(feature);
    setFormData({
      icon: feature.icon,
      title: feature.title,
      description: feature.description
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingFeature(null);
    setFormData({
      icon: 'SchoolIcon',
      title: '',
      description: ''
    });
  };

  const handleFormChange = (field: keyof FeatureFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const createFeature = async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/Features`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Failed to create feature');
      
      const createdFeature = await response.json();
      setFeatures(prev => [...prev, createdFeature]);
      closeDialog();
    } catch (err) {
      console.error('Failed to create feature:', err);
      setError('Failed to create feature');
    }
  };

  const updateFeature = async () => {
    if (!editingFeature) return;

    try {
      const response = await fetch(`${BASE_URL}/api/Features/${editingFeature.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Failed to update feature');
      
      const updatedFeature = await response.json();
      setFeatures(prev => 
        prev.map(f => f.id === editingFeature.id ? updatedFeature : f)
      );
      closeDialog();
    } catch (err) {
      console.error('Failed to update feature:', err);
      setError('Failed to update feature');
    }
  };

  const deleteFeature = async (id: number) => {
    if (!confirm('Are you sure you want to delete this feature?')) return;

    try {
      const response = await fetch(`${BASE_URL}/api/Features/${id}`, {
        method: 'DELETE'
      });

      if (!response.ok) throw new Error('Failed to delete feature');
      
      setFeatures(prev => prev.filter(f => f.id !== id));
    } catch (err) {
      console.error('Failed to delete feature:', err);
      setError('Failed to delete feature');
    }
  };

  const handleSave = () => {
    if (editingFeature) {
      updateFeature();
    } else {
      createFeature();
    }
  };

  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '50vh',
          fontSize: '1.1rem',
          color: 'text.secondary'
        }}
      >
        Loading features...
      </Box>
    );
  }

  if (error) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '50vh',
          color: 'error.main',
          fontSize: '1.1rem'
        }}
      >
        Error: {error}
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          // maxWidth: '1400px',
          width: '100%',
          mx: 'auto',
          mt: { xs: 2, md: 4 },
          mb: { xs: 2, md: 4 },
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >

        <Fade in={show} timeout={800}>
          <Box sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
             <Fade in timeout={800}>
            <Box className="text-center mb-12">
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4rem' },
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 3,
                  letterSpacing: '-0.02em'
                }}
              >
                Welcome to our school
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  fontSize: '1.1rem',
                  maxWidth: '600px',
                  mx: 'auto',
                  lineHeight: 1.6,
                  fontWeight: 400
                }}
              >
                We are a school that is dedicated to providing a high-quality education to our students.
              </Typography>
            </Box>
          </Fade>
            
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                justifyContent: 'center',
                alignItems: 'center',
                gap: { xs: 3, md: 4 },
                mt: 4,
                width: '80%',
                mediaQuery: {
                  xs: {
                    width: '100%'
                  }
                }
              }}
            >
              <ThemedFeatures 
                features={features} 
                onEdit={openEditDialog}
                onDelete={deleteFeature}
                show={show}
              />
            </Box>
          </Box>
        </Fade>

        {/* Add Feature Button */}
        {isAdminMode() && (
          <Fab
            color="primary"
            variant="extended"
            sx={{
              position: 'relative',
              bottom: 0,
              right: 0,
              zIndex: 1000,
              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              boxShadow: '0 8px 32px rgba(33, 150, 243, 0.3)',
              '&:hover': {
                background: 'linear-gradient(45deg, #1976D2 30%, #1BA8E0 90%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 12px 40px rgba(33, 150, 243, 0.4)'
              },
              transition: 'all 0.3s ease'
            }}
            onClick={openCreateDialog}
          >
            <AddIcon sx={{ mr: 1 }} />
            Add Feature
          </Fab>
        )}
      </Box>

      {/* Create/Edit Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={closeDialog}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Slide}
        BackdropComponent={Backdrop}
        BackdropProps={{
          sx: { 
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(8px)'
          }
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 24px 48px rgba(0, 0, 0, 0.15)',
            background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)'
          }
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            fontSize: '1.5rem',
            fontWeight: 600,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}
        >
          {editingFeature ? 'Edit Feature' : 'Create New Feature'}
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Icon</InputLabel>
              <Select
                value={formData.icon}
                label="Icon"
                onChange={(e) => handleFormChange('icon', e.target.value)}
                sx={{
                  borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.1)'
                  }
                }}
              >
                {iconOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Title"
              variant="outlined"
              value={formData.title}
              onChange={(e) => handleFormChange('title', e.target.value)}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '& fieldset': {
                    borderColor: 'rgba(0, 0, 0, 0.1)'
                  }
                }
              }}
            />

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Description"
              variant="outlined"
              value={formData.description}
              onChange={(e) => handleFormChange('description', e.target.value)}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '& fieldset': {
                    borderColor: 'rgba(0, 0, 0, 0.1)'
                  }
                }
              }}
            />
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 2, gap: 2 }}>
          <Button 
            onClick={closeDialog}
            variant="outlined"
            sx={{
              borderRadius: 2,
              px: 3,
              borderColor: 'rgba(0, 0, 0, 0.2)',
              color: 'text.secondary'
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            disabled={!formData.title.trim() || !formData.description.trim()}
            sx={{
              borderRadius: 2,
              px: 3,
              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              boxShadow: '0 4px 16px rgba(33, 150, 243, 0.3)',
              '&:hover': {
                background: 'linear-gradient(45deg, #1976D2 30%, #1BA8E0 90%)'
              }
            }}
          >
            {editingFeature ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

interface ThemedFeaturesProps {
  features: Feature[];
  onEdit: (feature: Feature) => void;
  onDelete: (id: number) => void;
  show: boolean;
}

const ThemedFeatures: React.FC<ThemedFeaturesProps> = ({ 
  features, 
  onEdit, 
  onDelete,
  show 
}) => {
  const theme = useTheme();

  return (
    <>
      {features.map((feature, index) => {
        const IconComponent = iconComponents[feature.icon] || SchoolIcon;
        
        return (
          <Fade 
            key={feature.id} 
            in={show} 
            timeout={800} 
            style={{ transitionDelay: `${index * 150}ms` }}
          >
            <Paper
              elevation={0}
              sx={{
                p: 4,
                height: '100%',
                maxWidth: '30%',
                display: 'flex',
                flexDirection: 'column',
                textAlign: 'center',
                position: 'relative',
                borderRadius: 3,
                background: `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
                border: `1px solid ${theme.palette.divider}`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                  transform: 'scaleX(0)',
                  transformOrigin: 'left',
                  transition: 'transform 0.3s ease'
                },
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
                  '&::before': {
                    transform: 'scaleX(1)'
                  }
                }
              }}
            >
              {/* Admin Controls */}
              {isAdminMode() && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    display: 'flex',
                    gap: 1,
                    opacity: 0,
                    transition: 'opacity 0.3s ease',
                    '.MuiPaper-root:hover &': {
                      opacity: 1
                    }
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(feature);
                    }}
                    sx={{ 
                      backgroundColor: 'rgba(33, 150, 243, 0.1)',
                      borderRadius: 2,
                      '&:hover': { 
                        backgroundColor: 'rgba(33, 150, 243, 0.2)',
                        transform: 'scale(1.1)'
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <EditIcon fontSize="small" color="primary" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(feature.id);
                    }}
                    sx={{ 
                      backgroundColor: 'rgba(244, 67, 54, 0.1)',
                      borderRadius: 2,
                      '&:hover': { 
                        backgroundColor: 'rgba(244, 67, 54, 0.2)',
                        transform: 'scale(1.1)'
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <DeleteIcon fontSize="small" color="error" />
                  </IconButton>
                </Box>
              )}

              <Box 
                sx={{ 
                  mb: 3, 
                  mt: isAdminMode() ? 2 : 0,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: 80,
                  height: 80,
                  mx: 'auto',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                  transition: 'all 0.3s ease',
                  '.MuiPaper-root:hover &': {
                    transform: 'scale(1.1)',
                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)'
                  }
                }}
              >
                <IconComponent 
                  sx={{ 
                    fontSize: 40,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }} 
                />
              </Box>
              
              <Typography 
                variant="h6" 
                component="h3"
                sx={{ 
                  mb: 2,
                  fontWeight: 600,
                  fontSize: '1.25rem',
                  color: theme.palette.text.primary,
                  lineHeight: 1.3
                }}
              >
                {feature.title}
              </Typography>
              
              <Typography 
                variant="body2" 
                sx={{ 
                  color: theme.palette.text.secondary,
                  lineHeight: 1.6,
                  flexGrow: 1,
                  fontSize: '0.95rem'
                }}
              >
                {feature.description}
              </Typography>
            </Paper>
          </Fade>
        );
      })}
    </>
  );
};

export default WelcomeSection;